<template>
  <v-container
    id="flight-glider-tables-view"
    fluid
    tag="section"
  >
    <material-card
      :avatar="require('@/assets/glider.png')"
      color="accent"
      :title="page_title"
      help="flight_glider"
    >
      <v-card-title>
        <v-btn
          v-if="editor"
          v-shortkey.once="['f2']"
          color="primary"
          class="mb-2 ml-2"
          @shortkey="edit({id: -1})"
          @click="edit({id: -1})"
        >
          {{ $t('new_item',{ item: $t('flight') }) }}
        </v-btn>
        <v-btn
          color="green"
          class="mb-2 ml-2"
          @click="editPrivate({id: -1})"
        >
          {{ $t('new_private_flight') }}
        </v-btn>
        <daily-flight-plane-informations
          v-if="editor && !filter_year"
          :date="flight_date"
        />
        <daily-flight-user-informations
          v-if="editor && !filter_year"
          :date="flight_date"
        />
        <export-btn
          v-if="editor"
          source-type="flight_glider"
          :params="{ date: flight_date, year: flight_year, filterYear: filter_year}"
        />
        <v-row class="ma-0">
          <v-spacer v-if="!$vuetify.breakpoint.mdAndDown" />
          <v-switch
            v-if="editor"
            v-model="filter_year"
            class="mx-2"
            :label="$t('show_year')"
            hide-details
          />
          <date-field
            v-if="!filter_year"
            v-model="flight_date"
            field-name="date"
            :events="flight_dates"
            hide-details
            class-self="mr-1"
          />
          <v-select
            v-if="filter_year"
            v-model="flight_year"
            :items="years"
            :label="$t('fields.season')"
            hide-details
            class="mx-2"
          />
          <v-text-field
            v-model="search"
            append-icon="$search"
            class="ml-auto"
            hide-details
            :label="$t('search_records')"
            single-line
            style="max-width: 400px;"
          />
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="computed_headers"
          :items="items"
          :loading="loading"
          :loading-text="$t('loading')"
          :options.sync="options"
          :server-items-length="total"
          :footer-props="footerProps"
        >
          <template #item.date="{item}">
            {{ item.date | dateFormat }}
          </template>

          <template #item.takeoff_time="{item}">
            {{ item.takeoff_time | timeFormat }} - {{ item.landing_time | timeFormat }}
          </template>

          <template #item.plane="{item}">
            {{ item.plane.reg_num }} - {{ item.plane.model }}
          </template>

          <template #item.flight_time="{item}">
            {{ timediff(item.takeoff_time, item.landing_time) }}
          </template>

          <template #item.takeoff_type_id="{item}">
            {{ $t( 'types.'+item.takeoff_type.name) }} {{ item.tow_altitude ? '(' + item.tow_altitude + 'm)': '' }}
          </template>

          <template #item.actions="{item}">
            <v-btn
              v-if="item.ogn_url != null"
              icon
              color="blue"
              :href="item.ogn_url"
              target="_blank"
            >
              <v-icon v-html="'$map'" />
            </v-btn>

            <v-tooltip
              v-if="item.transactions.length>0"
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="checkModule('financial')"
                  class="px-0 ml-1"
                  elevation="0"
                  min-width="0"
                  small
                  text
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon v-html="'$transactions'" />
                </v-btn>
              </template>
              <pre
                v-for="transaction in item.transactions"
                :key="transaction.id"
              >{{ transCalc(transaction.calc) | pretty }}</pre>
            </v-tooltip>
            <tooltip-btn
              v-for="(action, i) in actions"
              :key="i"
              :color="action.color"
              :disabled="action.disable && item.validation_at !== null"
              :icon="action.icon"
              :tooltip="action.tooltip"
              @click="action.func(item)"
            />
            <tooltip-btn
              v-if="editor && item.validation_at === null"
              color="edit"
              icon="$check"
              tooltip="i_certify"
              @click="clickValidateFlight(item)"
            />
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>
  </v-container>
</template>

<script>
  // Utilities
  import { get, call, sync } from 'vuex-pathify'
  import moment from 'moment'
  import { format } from 'date-fns'
  import DailyFlightPlaneInformations from '@/layouts/default/infos/DailyFlightPlaneInfo'
  import DailyFlightUserInformations from '@/layouts/default/infos/DailyFlightUserInfo'

  export default {
    name: 'FlightGlider',
    components: {
      DailyFlightUserInformations,
      DailyFlightPlaneInformations,
    },
    data () {
      return {
        items: [],
        // table
        footerProps: {
          itemsPerPageOptions: [10, 20, 25, 50, 100],
        },
        total: 0,
        timer: null,
        flight_dates: [],
        years: [],
        // flight_date: '',
        // flight_year: '',
        // filter_year: false,
      }
    },
    computed: {
      loading: get('app/loading'),
      profile: get('auth/profile'),
      options: sync('flightGlider/options'),
      search: sync('flightGlider/search'),
      flight_date: sync('flightGlider/flight_date'),
      filter_year: sync('flightGlider/filter_year'),
      flight_year: sync('flightGlider/flight_year'),
      headers () {
        return [
          {
            hide_small: true,
            text: this.$t('fields.airport'),
            sortable: false,
            value: 'takeoff_airport.name',
          },
          {
            hide_small: true,
            text: this.$t('fields.date'),
            align: 'start',
            sortable: false,
            value: 'date',
          },
          {
            text: this.$t('fields.time'),
            sortable: true,
            value: 'takeoff_time',
          },
          {
            text: this.$t('fields.pic'),
            sortable: false,
            value: 'pic.name',
          },
          {
            text: this.$t('fields.sic'),
            sortable: false,
            value: 'sic.name',
          },
          {
            text: this.$t('fields.plane'),
            sortable: false,
            value: 'plane',
          },
          {
            hide_small: true,
            text: this.$t('fields.flight_time'),
            sortable: false,
            value: 'flight_time',
          },
          {
            hide_small: true,
            text: this.$t('fields.takeoff_type'),
            sortable: true,
            value: 'takeoff_type_id',
          },
          {
            text: this.$t('actions'),
            value: 'actions',
            sortable: false,
            align: 'right',
          },
        ]
      },
      actions () {
        return [
          // { color: 'info', icon: '$info' },
          {
            color: 'edit',
            icon: '$edit',
            tooltip: 'tooltip_edit',
            disable: !this.editor,
            func: this.edit,
          },
        ]
      },
      page_title () { return this.$t('ods') + ' - ' + this.$t('glider') },
      editor () { return this.profile.can['ods-edit'] },
      // editor () { return false },
      last_date: get('flightGlider/last_date'),
      computed_headers () { return this.headers.filter(h => h.hide_small !== this.$vuetify.breakpoint.mdAndDown) },
    },
    watch: {
      search: {
        handler () {
          if (this.timer) {
            clearTimeout(this.timer)
            this.timer = null
          }
          this.timer = setTimeout(() => {
            this.options.page = 1
            this.load()
          }, 800)
        },
        deep: true,
      },
      options: {
        handler () {
          this.load()
        },
        deep: true,
      },
      flight_date: {
        handler () {
          this.load()
        },
        deep: true,
      },
      filter_year: {
        handler () {
          this.load()
        },
        deep: true,
      },
      flight_year: {
        handler () {
          this.load()
        },
        deep: true,
      },
    },
    mounted () {
      for (let i = 1950; i <= new Date().getFullYear(); i++) {
        this.years.unshift(i)
      }
      this.fetchDatasource({ sourceType: 'flight-dates' }).then(response => {
        this.flight_dates = response.map(m => m.date) //  { date: '2021-15-15', events: ['2021-15-15'] }
        // console.log('status:')
        // console.log(format(new Date(), 'yyyy-MM-dd'));
        const today = format(new Date(), 'yyyy-MM-dd')
        if (this.flight_date === '') {
          this.flight_date = this.last_date ?? (response.length !== 0 ? response[0].date : today)
        }
        /* this.flight_years = response.map(m => m.date.substr(0, 4))
        if (this.flight_year === '') {
          this.flight_year = this.flight_years[0]
        } */
        // this.filter_year = !this.editor

        // console.log(this.flight_date)
        // console.log(response[0].date)
      })
    },
    methods: {
      getList: call('flightGlider/search'),
      flightValidate: call('flightGlider/validate'),
      fetchDatasource: call('datasource/fetchDatasource'),
      async load () {
        if (this.flight_date === '') { return }
        try {
          const { sortBy, sortDesc, page, itemsPerPage } = this.options
          await this.getList({
            page: page,
            per_page: itemsPerPage,
            sort: {
              field: sortBy,
              order: sortDesc,
            },
            date: this.flight_date,
            search: this.search,
            filterYear: this.filter_year,
            year: this.flight_year,
            private: !this.editor,
          }).then(response => {
            this.items = response.data
            this.total = response.total
          }).catch(e => {
            console.log(e)
          })
        } catch (e) {
        }
      },
      edit (item) {
        if (!this.editor) {
          this.$router.push(`/flight/glider-private/${item.id}`).catch(() => {})
        } else {
          this.$router.push(`/flight/glider/${item.id}`).catch(() => {})
        }
      },
      editPrivate (item) {
        this.$router.push(`/flight/glider-private/${item.id}`).catch(() => {})
      },
      timediff (landingTime, takeoffTime) {
        return moment.utc(moment(takeoffTime, 'HH:mm:ss').diff(moment(landingTime, 'HH:mm:ss'))).format('HH:mm')
      },
      clickValidateFlight (item) {
        this.flightValidate(item).then(response => {
          item.validation_at = Date.now()
          // this.load()
        })
      },
      checkModule (payload) {
        return this.profile.organization.modules.filter(m => m.name === payload).length > 0
      },
      transCalc (calc) {
        const arrayFailed = Object.entries(JSON.parse(calc)).map((arr) => (
          this.$t('fields.' + arr[0], { currency: this.profile.organization.currency.name }) + ': ' + arr[1]
        ))
        return arrayFailed
      },
    },
  }
</script>
